import { useMemo } from 'react';
import useStore from '@components/MainStage/store';
import { DependencyTypes, WeightTypes } from '@constants/entities/relationship';
import { canvasMap } from '@utils/canvas/CanvasMap';

const useCurrentANDRelations = (
  selectedDependency: DependencyTypes | undefined,
  editMode: boolean,
) => {
  const { getActiveEdge, edges } = useStore();
  const activeEdge = getActiveEdge();

  const { dependency, weight_type } = activeEdge?.data?.dto ?? {};

  const relationsAND = useMemo(() => {
    const targetNode = activeEdge?.target
      ? canvasMap.nodeMap.get(activeEdge?.target)
      : undefined;

    const targetSubLayer = targetNode?.parentNode;

    return edges.filter((edge) => {
      const isLocalEdge = edge.id.startsWith('local');
      const isSameSourceNode = edge.source === activeEdge?.source;
      const isANDDependency =
        edge.data?.dto?.dependency === DependencyTypes.AND;

      const currentEdgeTargetNode = canvasMap.nodeMap.get(edge?.target);

      const isSameTargetSubLayer =
        currentEdgeTargetNode?.parentNode === targetSubLayer;

      return (
        !isLocalEdge &&
        isSameSourceNode &&
        isSameTargetSubLayer &&
        isANDDependency
      );
    });
  }, [edges, activeEdge?.source, activeEdge?.id]);

  const { autoCount, manualSum } = useMemo(() => {
    return relationsAND.reduce(
      (acc, edge) => {
        const isWeightAuto = edge.data?.dto?.weight_type === WeightTypes.Auto;

        if (isWeightAuto && edge.id !== activeEdge?.id) {
          acc.autoCount += 1;
        } else {
          acc.manualSum += edge.data?.dto?.weight || 0;
        }

        return acc;
      },
      { autoCount: 0, manualSum: 0 },
    );
  }, [relationsAND, editMode, activeEdge, activeEdge?.id]);

  const isLastANDAuto =
    editMode &&
    dependency === DependencyTypes.AND &&
    weight_type === WeightTypes.Auto &&
    relationsAND.length > 1 &&
    !autoCount;

  const isFirstANDAuto =
    selectedDependency === DependencyTypes.AND && !autoCount;

  return {
    relations: relationsAND,
    autoCount,
    manualSum,
    isFirstANDAuto,
    isLastANDAuto,
  };
};

export default useCurrentANDRelations;
